import { useState } from "react";
import { GuestTenantSearch } from "./GuestTenantSearch";
import { FounderBanner } from "./FounderBanner";
import { Logo } from "./Logo";
import { BrandBackdrop, Tagline } from "./AuthLayout";
import { usePromo } from "../utils/promo";
import { useAuth } from "../context/AuthContext";

// Guest Dashboard Component (Limited functionality)
const GuestDashboard = ({ setAuthMode }) => {
  const [activeTab, setActiveTab] = useState('search');
  const promo = usePromo();
  const { exitGuestMode } = useAuth();
  // Leave the guest view so the sign-up form actually shows
  const goRegister = () => {
    exitGuestMode();
    setAuthMode('register');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white text-left">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md border-b border-amber-100" style={{position: 'relative', zIndex: 300}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center gap-3 py-3 sm:py-0 sm:h-16">
            <Logo subtitle="Consulta r&aacute;pida sin cuenta" />
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="hidden md:block text-right">
                <p className="text-sm text-amber-700/80">Vista limitada</p>
              </div>

              {/* Register Button in header */}
              <button
                onClick={goRegister}
                className="bg-amber-950 text-amber-50 px-5 py-2.5 rounded-full hover:bg-amber-900 font-semibold text-sm transition-colors shadow-sm"
              >
                Registrarme
              </button>
            </div>
          </div>
        </div>
      </header>

      <FounderBanner onCta={goRegister} />

      {/* Welcome Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8">
        <section className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-amber-500 via-amber-600 to-amber-800 text-white p-6 sm:p-10 shadow-xl shadow-amber-300/40">
          <BrandBackdrop />
          <div className="relative max-w-3xl">
            <p className="text-amber-100 text-xs font-semibold uppercase tracking-[0.18em]">Consulta r&aacute;pida &middot; sin cuenta</p>
            <Tagline className="mt-3 text-3xl sm:text-5xl" />
            <p className="mt-4 text-amber-50/95 text-base sm:text-lg">Sin cuenta ves si existen evaluaciones. Con tu cuenta gratis ves el detalle, el sem&aacute;foro de riesgo y los comentarios de otros propietarios.</p>
          </div>
        </section>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-[2rem] shadow-sm border border-amber-100">
          {/* Navigation Tabs */}
          <div className="p-2 sm:p-3 border-b border-amber-100">
            <nav className="flex gap-1 sm:gap-2 overflow-x-auto no-scrollbar">
              <button
                onClick={() => setActiveTab('search')}
                className={`flex-shrink-0 py-2.5 px-4 sm:px-5 rounded-full font-semibold text-sm transition-colors ${
                  activeTab === 'search'
                    ? 'bg-amber-950 text-amber-50 shadow-md'
                    : 'text-amber-900/70 hover:bg-amber-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span>Buscar Inquilinos</span>
                  <span className="bg-emerald-100 text-emerald-800 text-xs px-2 py-1 rounded-full font-bold">GRATIS</span>
                </div>
              </button>
              
              {/* Disabled tabs with explanations */}
              <div className="relative group flex-shrink-0">
                <button
                  disabled
                  className="py-2.5 px-4 sm:px-5 rounded-full text-amber-300 font-semibold text-sm cursor-not-allowed opacity-50"
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                    <span>Evaluar Inquilino</span>
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                </button>
                <div className="absolute top-full left-0 mt-2 w-72 sm:w-80 max-w-[90vw] bg-white rounded-lg shadow-xl border border-gray-200 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto">
                  <div className="p-4">
                    <h4 className="font-bold text-gray-800 mb-2"> Funci&oacute;n Premium</h4>
                    <p className="text-sm text-gray-600 mb-3">Esta funci&oacute;n permite evaluar y calificar inquilinos problem&aacute;ticos. {promo.free_mode
                      ? 'Requiere cuenta registrada. Gratis por tiempo limitado con tu Acceso Fundador.'
                      : `Requiere cuenta registrada y pago de $${promo.rating_price_usd} por evaluación.`}</p>
                    <button
                      onClick={goRegister}
                      className="w-full bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 transition-colors duration-200 font-medium"
                    >
                      Registrarme para Acceder
                    </button>
                  </div>
                </div>
              </div>

              <div className="relative group flex-shrink-0">
                <button
                  disabled
                  className="py-2.5 px-4 sm:px-5 rounded-full text-amber-300 font-semibold text-sm cursor-not-allowed opacity-50"
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <span>Panel de An&aacute;lisis</span>
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                </button>
                <div className="absolute top-full left-0 mt-2 w-72 sm:w-80 max-w-[90vw] bg-white rounded-lg shadow-xl border border-gray-200 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto">
                  <div className="p-4">
                    <h4 className="font-bold text-gray-800 mb-2"> An&aacute;lisis Profesional</h4>
                    <p className="text-sm text-gray-600 mb-3">Panel completo con estad&iacute;sticas, reportes PDF, an&aacute;lisis comparativo y gesti&oacute;n de evaluaciones. Solo para usuarios registrados.</p>
                    <button
                      onClick={goRegister}
                      className="w-full bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 transition-colors duration-200 font-medium"
                    >
                      Registrarme para Acceder
                    </button>
                  </div>
                </div>
              </div>
            </nav>
          </div>

          <div className="p-4 sm:p-8">
            {activeTab === 'search' && <GuestTenantSearch onRegister={goRegister} />}
          </div>
        </div>
      </main>
    </div>
  );
};

export { GuestDashboard };
